/**
 * Add Calendar - Club admin interface for creating new calendars
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import apiService from '../../services/api';
import { AdminPageLayout, FormInput, FormTextArea, FormSelect, FormCheckbox, FormColorPicker, Button, FormActions, Badge } from '../../components';
import { ErrorBanner } from '../../components/common';
import { useFormValidation } from '../../hooks/useFormValidation';
import './CalendarForm.css';

const AddCalendar = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  const calendarScopes = [
    { value: 'club', label: 'Club - Club-wide calendar managed by club admin' },
    { value: 'branch', label: 'Branch - Branch calendar managed by branch admin' },
    { value: 'program', label: 'Program - Program calendar managed by club admin' },
    { value: 'team', label: 'Team - Team calendar managed by coach' },
  ];

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Phoenix', label: 'Mountain Time - Arizona (no DST)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' }
  ];

  const tagOptions = [
    'recreational', 'competitive', 'youth', 'adult', 'camps', 'clinics',
    'indoor', 'outdoor', 'travel', 'premier', 'development'
  ];

  const {
    formData,
    errors,
    touched,
    isSubmitting,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit: handleFormSubmit,
    setFieldValue
  } = useFormValidation({
    initialValues: {
      name: '',
      description: '',
      scope: 'club',  // NEW: renamed from calendar_scope
      is_active: true,
      color_code: '#1565C0',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      tags: []
    },
    validationSchema: (values) => {
      const newErrors = {};

      if (!values.name.trim()) {
        newErrors.name = 'Calendar name is required';
      }

      return newErrors;
    },
    onSubmit: async (values) => {
      try {
        // For club scope, backend will auto-set scope_id from user context
        // For other scopes (team/program/branch), we would need to provide scope_id
        // TODO: Add team/program/branch selection when creating those scope calendars

        const calendarData = {
          name: values.name,
          description: values.description,
          scope: values.scope,  // "club", "branch", "program", or "team"
          // scope_id not needed for club scope - backend auto-sets from user context
          visibility_roles: ['all'],  // everyone can see by default
          is_active: values.is_active,
          color_code: values.color_code,
          timezone: values.timezone,
          tags: Array.isArray(values.tags) ? values.tags : []
        };

        console.log('Creating calendar with data:', calendarData); // Debug log

        await apiService.createCalendar(calendarData);
        navigate('/admin/calendars');
      } catch (err) {
        console.error('Calendar creation error:', err); // Debug log
        setError(err.message || 'Failed to create calendar');
        throw err;
      }
    }
  });

  const handleTagToggle = (tag) => {
    const currentTags = formData.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    setFieldValue('tags', newTags);
  };

  return (
    <AdminPageLayout
      title="New Calendar"
      subtitle="Create a new calendar for your club"
      backPath="/admin/calendars"
    >
      <div className="add-calendar-page">
        {error && (
          <ErrorBanner
            type="error"
            message={error}
            onDismiss={() => setError(null)}
          />
        )}

        <form onSubmit={handleFormSubmit} className="calendar-form">
          <div className="form-section">
            <h3>Calendar Details</h3>

            <div className="form-row">
              <FormInput
                label="Calendar Name"
                name="name"
                value={formData.name}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.name ? errors.name : ''}
                placeholder="e.g., Fall 2024 Season, Summer Camps"
                required
              />

              <FormSelect
                label="Calendar Scope"
                name="scope"
                value={formData.scope}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.scope ? errors.scope : ''}
                options={calendarScopes}
                required
              />
            </div>

            <FormTextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.description ? errors.description : ''}
              placeholder="Describe the calendar purpose and schedule"
              rows={3}
            />
          </div>

          <div className="form-section">
            <h3>Visibility Settings</h3>

            <div className="visibility-info">
              By default, calendars are visible to all members of the entity scope (club/branch/program/team).
              You can customize visibility roles after creating the calendar.
            </div>
          </div>

          <div className="form-section">
            <h3>Calendar Settings</h3>

            <div className="form-row">
              <FormColorPicker
                label="Color Code"
                name="color_code"
                value={formData.color_code}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.color_code ? errors.color_code : ''}
              />

              <FormSelect
                label="Timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.timezone ? errors.timezone : ''}
                options={timezones}
              />
            </div>

            <FormCheckbox
              label="Active"
              name="is_active"
              checked={formData.is_active}
              onChange={(e) => setFieldValue('is_active', e.target.checked)}
            />
            <p className="field-hint" style={{ marginTop: '-12px', marginLeft: '28px' }}>
              Only active calendars are visible and can be used
            </p>
          </div>

          <div className="form-section">
            <h3>Tags</h3>
            <div className="tag-selector">
              {tagOptions.map(tag => (
                <Badge
                  key={tag}
                  variant={formData.tags.includes(tag) ? 'primary' : 'outline'}
                  size="md"
                  className="tag-badge"
                  onClick={() => handleTagToggle(tag)}
                  role="switch"
                  aria-pressed={formData.tags.includes(tag)}
                  aria-label={`${tag} tag`}
                  style={{ cursor: 'pointer' }}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <FormActions
              onCancel={() => navigate('/admin/calendars')}
              submitLabel={isSubmitting ? 'Creating...' : 'Create Calendar'}
              isSubmitting={isSubmitting}
              submitIcon={<Check size={16} />}
              cancelIcon={<X size={16} />}
            >
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/admin/calendars')}
                disabled={isSubmitting}
              >
                <X size={16} style={{ marginRight: '8px' }} />
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                <Check size={16} style={{ marginRight: '8px' }} />
                {isSubmitting ? 'Creating...' : 'Create Calendar'}
              </Button>
            </FormActions>
          </div>
        </form>
      </div>
    </AdminPageLayout>
  );
};

export default AddCalendar;
